import { Link } from 'react-router-dom';

const partners = [
  { label: 'Stripe Connect', style: { fontWeight: 700, fontSize: 14, letterSpacing: '.5px' } as React.CSSProperties, bg: '#FAF7F2', color: '#0F0F12' },
  { label: 'PSD2 SCA',       style: { fontFamily: 'Cormorant Garamond, serif', fontWeight: 600, fontSize: 15, letterSpacing: '1px' }, bg: '#FAF7F2', color: '#0F0F12' },
  { label: 'InPost · Locker',style: { fontWeight: 600, fontSize: 13, letterSpacing: '1px' }, bg: '#A66018', color: '#FAF7F2' },
  { label: 'UK GDPR',        style: { fontFamily: 'Cormorant Garamond, serif', fontWeight: 500, fontSize: 14, letterSpacing: '2px' }, bg: '#FAF7F2', color: '#0F0F12' },
];

const lifecycle = [
  { state: 'CREATED',   head: 'Order opened',     body: 'Card authorised, no charge yet' },
  { state: 'PAID_HELD', head: 'Funds in payment hold',  body: 'Souqli holds the funds; the seller cannot touch them' },
  { state: 'IN_LOCKER', head: 'In the locker',    body: 'PIN sent to buyer' },
  { state: 'COLLECTED', head: 'Buyer collected',  body: '48h dispute window opens' },
  { state: 'RELEASED',  head: 'Seller paid',      body: 'Transferred via Stripe to seller account' },
];

export default function Trust() {
  return (
    <div className="bg-cream">
      {/* Hero with photograph + Trust score card */}
      <header className="section-dark relative bg-ink text-cream overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1556228720-195a672e8a03?w=1920&h=600&fit=crop&auto=format&q=80"
          alt=""
          aria-hidden
          onError={(e) => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1920&h=600&fit=crop&auto=format&q=80'; }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(15,15,18,.95), rgba(15,15,18,.7) 60%, rgba(15,15,18,.45))' }} />
        <div className="absolute inset-0 bg-gold-mesh opacity-50" />
        <div className="absolute inset-0 dotgrid-light bg-[length:24px_24px] opacity-30" />
        <div className="relative max-w-[1240px] mx-auto px-6 py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="stamp text-gold mb-3 text-[10px]">TRUST AT SOUQLI</div>
            <h1 className="display text-5xl md:text-6xl leading-[0.98] max-w-3xl">
              Four invisible safeguards behind every transaction.
            </h1>
            <p className="mt-5 max-w-[60ch] text-cream/80 leading-relaxed">
              We built Souqli so you never have to take a stranger on faith. Verification, the payment hold, locker delivery and mediated disputes all work in your favour, by default.
            </p>
            <div className="mt-6 flex flex-wrap gap-6 text-sm text-cream/80">
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-ok" /> Funds held until you confirm the item</span>
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-gold" /> Every seller ID-verified</span>
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-clay-light" /> Disputes mediated by our team</span>
            </div>
          </div>
          <div className="lg:col-span-5 lg:flex lg:justify-end">
            <div className="card-glass p-5 max-w-[340px]" style={{ background: 'rgba(15,15,18,.55)', borderColor: 'rgba(201,169,97,.3)' }}>
              <div className="stamp text-gold text-[9px]">HOW YOUR MONEY IS PROTECTED</div>
              <p className="mt-3 text-[13px] text-cream/85 leading-relaxed">
                Your payment is held securely until you confirm receipt and released to the seller only once you confirm the item has arrived as described.
              </p>
              <p className="mt-3 text-[13px] text-cream/85 leading-relaxed">
                Every seller is ID-verified before they can list, and if anything is not right, our team steps in to resolve it.
              </p>
            </div>
          </div>
        </div>
        <div className="gold-rule-thick" />
      </header>

      {/* Partner / certification band */}
      <section className="bg-cream-deep border-b border-warmline">
        <div className="max-w-[1240px] mx-auto px-6 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          <span className="stamp text-mute text-[9px]">SECURITY &amp; COMPLIANCE</span>
          {partners.map((p) => (
            <span
              key={p.label}
              className="inline-flex items-center justify-center h-9 px-4 rounded shadow-sm"
              style={{ background: p.bg, color: p.color, ...p.style }}
            >{p.label}</span>
          ))}
        </div>
      </section>

      {/* Four pillars */}
      <div className="max-w-[1240px] mx-auto px-6 py-16 grid md:grid-cols-2 gap-6">
        <Pillar id="verification" n="01" title="Verification" tone="bg-cream">
          Every listing requires two timestamped photographs, and for electronics, an IMEI or
          serial number. Electronics are screened against duplicate and known-stolen serials,
          and flagged for manual review before they go live.
        </Pillar>
        <Pillar id="payment hold" n="02" title="Stripe & payouts" tone="bg-gold/10">
          When you buy, your card is charged but funds are held by Souqli, not the seller.
          The seller is paid only after you confirm collection (or 24h after locker pickup
          if no dispute is raised). Refunds are immediate from the held balance.
        </Pillar>
        <Pillar id="delivery" n="03" title="Locker delivery" tone="bg-clay/10">
          Sellers drop parcels into a partner locker (InPost-style). You get a one-time PIN
          by SMS to collect at any time, day or night. No missed couriers, no porch theft,
          no patience required.
        </Pillar>
        <Pillar id="disputes" n="04" title="Disputes" tone="bg-sage/10">
          If an item is not as described, open a return within 48 hours of collection.
          Souqli mediates within 3 working days; funds remain held until resolution.
        </Pillar>
      </div>

      {/* Stripe lifecycle */}
      <section className="section-dark bg-ink text-cream py-16 relative overflow-hidden">
        <div className="absolute inset-0 dotgrid-light bg-[length:24px_24px] opacity-40" />
        <div className="relative max-w-[1240px] mx-auto px-6">
          <div className="stamp text-gold mb-3">Stripe lifecycle</div>
          <h2 className="display text-4xl md:text-5xl mb-12 max-w-3xl">Where your money sits at every step.</h2>
          <div className="relative">
            <div className="absolute left-0 right-0 top-6 h-px bg-gold/30" />
            <ol className="grid md:grid-cols-5 gap-6 relative">
              {lifecycle.map((s) => (
                <li key={s.state}>
                  <div className="w-3 h-3 rounded-full bg-gold mb-4 ring-4 ring-ink relative z-10" />
                  <div className="stamp text-gold">{s.state}</div>
                  <div className="display text-xl mt-1.5">{s.head}</div>
                  <p className="mt-2 text-cream/70 text-sm leading-relaxed">{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1240px] mx-auto px-6 py-16 text-center">
        <div className="stamp text-gold-dark">Ready when you are</div>
        <h2 className="display text-4xl md:text-5xl mt-3 max-w-2xl mx-auto">Buy with the safest protections in resale.</h2>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/c/all" className="btn btn-lg btn-primary">Shop the edit</Link>
          <Link to="/dashboard/sell" className="btn btn-lg btn-outline">List your first item</Link>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="section-dark py-16 border-t border-cream/10 bg-ink">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <h2 className="display text-2xl text-cream text-center mb-10">Frequently asked questions</h2>
          {[
            { q: 'How does payment work?',                a: 'When you buy, your payment is held securely by Stripe, and not released to the seller until you confirm receipt, which protects both sides.' },
            { q: 'How long does delivery take?',          a: 'Most items arrive within 2-4 days via our partner locker network. You collect with a one-time PIN.' },
            { q: 'What if the item is not as described?', a: 'Raise a dispute within 7 days of collection. Our team reviews evidence from both sides and resolves it fairly, usually within 48 hours.' },
            { q: 'How do sellers get paid?',              a: 'Once you confirm receipt, funds are released immediately to the seller wallet via Stripe. Same-day withdrawal is available.' },
            { q: 'Are sellers verified?',                 a: 'All sellers complete an ID check powered by Stripe Identity before they can list. Gold and Curated tier sellers are additionally vetted by our team.' },
            { q: 'What is the Souqli Member plan?',       a: 'For £10.99/month, members pay no buyer protection fee on up to 12 orders and £300 of spend each month (then 3.5% + 60p, versus 4.5% + 70p for guests), earn a £4 delivery voucher when they spend £300+, and see new drops a full hour early.' },
          ].map(({ q, a }) => (
            <div key={q} className="border-b border-cream/10 py-5">
              <p className="text-cream font-medium text-sm mb-2">{q}</p>
              <p className="text-cream/55 text-sm leading-relaxed">{a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Pillar({ id, n, title, tone, children }: { id: string; n: string; title: string; tone: string; children: React.ReactNode }) {
  return (
    <section id={id} className={`notched border border-warmline ${tone} p-8 relative`}>
      <div className="display text-7xl text-gold/30 absolute top-3 right-5 leading-none">{n}</div>
      <div className="stamp text-gold-dark">{n}</div>
      <h2 className="display text-3xl mt-2">{title}</h2>
      <p className="mt-4 text-ink-700 leading-relaxed">{children}</p>
    </section>
  );
}
